import { Component, Input, Output, EventEmitter } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { toContextArray, firstContextRecord } from 'src/app/common/bhub-common-module/utils/context-array.util';
import { ViewManager, CalcOperand, InlineCalcOperand, ListCalcFilter } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';

@Component({
  selector: 'bhub-pdf-list-calc-cell',
  standalone: false,
  templateUrl: './pdf-list-calc-cell.component.html',
  styleUrl: './pdf-list-calc-cell.component.scss'
})
export class PdfListCalcCellComponent extends BaseCellComponent {
  @Input() viewManager: ViewManager | null = null;
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();

  calculatedValue: number | null = null;
  formattedValue: string = '';
  label: string = '';
  showLabel: boolean = true;
  fontSize: number = 14;
  fontWeight: string = 'normal';
  hasOverride: boolean = false;

  private lastStoredValue: number | null = null;

  protected initializeCell(): void {
    this.loadConfig();
    this.calculate();
  }

  protected onCellOrDataChange(): void {
    this.loadConfig();
    this.calculate();
  }

  protected onCellUpdate(): void {
    this.loadConfig();
    this.calculate();
  }

  protected onDataSet(data: any): void {
    // Not used - data comes from context
  }

  override reloadData(): void {
    this.calculate();
  }

  private loadConfig(): void {
    this.label = this.getConfigValue('pdfListCalcLabel', '');
    this.showLabel = this.getConfigValue('pdfListCalcShowLabel', true);
    this.fontSize = this.getConfigValue('pdfListCalcFontSize', 14);
    this.fontWeight = this.getConfigValue('pdfListCalcFontWeight', 'normal');
  }

  private calculate(): void {
    // If there's a manual override from PDF edit mode, use it and skip recalculation
    const overrideValue = this.getConfigValue('pdfOverrideValue', undefined);
    if (overrideValue !== undefined) {
      this.hasOverride = true;
      this.formattedValue = overrideValue;
      this.calculatedValue = parseFloat(overrideValue) || 0;
      return;
    }
    this.hasOverride = false;

    const contextVariable: string = this.getConfigValue('pdfListCalcContextVariable', '');
    const fieldName: string = this.getConfigValue('pdfListCalcFieldName', '');
    const operation: string = this.getConfigValue('pdfListCalcOperation', 'sum');
    const format: string = this.getConfigValue('pdfListCalcFormat', 'number');
    const decimalPlaces: number = this.getConfigValue('pdfListCalcDecimalPlaces', 2);

    if (!contextVariable || !fieldName || !this.viewManager) {
      this.calculatedValue = null;
      this.formattedValue = '';
      return;
    }

    const contextData = this.viewManager.contextData[contextVariable];
    if (!contextData) {
      this.calculatedValue = null;
      this.formattedValue = '';
      return;
    }

    // Get the array of items
    const sourceItems = toContextArray(contextData);
    if (!sourceItems) {
      this.calculatedValue = null;
      this.formattedValue = '';
      return;
    }
    let items: any[] = sourceItems;

    // Apply row filters before aggregation
    const filters: ListCalcFilter[] = this.getConfigValue('pdfListCalcFilters', []);
    if (filters && filters.length > 0) {
      items = items.filter(item => this.matchesFilters(item, filters));
    }

    // Collect per-row computed values from the list
    const inlineOperands: InlineCalcOperand[] = this.getConfigValue('pdfListCalcInlineOperands', []);
    const values: number[] = [];
    for (const item of items) {
      const rawValue = item[fieldName];

      // For COUNT, we just need the field to exist (any non-null value counts)
      if (operation === 'count') {
        if (rawValue !== null && rawValue !== undefined && String(rawValue) !== '') {
          values.push(1);
        }
        continue;
      }

      if (rawValue === null || rawValue === undefined) continue;
      let rowValue = this.parseNumericValue(rawValue);
      if (rowValue === null) continue;

      // Apply inline operations using fields from the same row
      if (inlineOperands && inlineOperands.length > 0) {
        for (const op of inlineOperands) {
          let opValue: number | null;
          if (op.sourceType === 'value') {
            opValue = op.literalValue ?? null;
          } else {
            const raw = item[op.fieldName];
            opValue = raw !== null && raw !== undefined ? this.parseNumericValue(raw) : null;
          }
          if (opValue === null) continue;

          switch (op.operator) {
            case '-': rowValue -= opValue; break;
            case '*': rowValue *= opValue; break;
            case '/': rowValue = opValue !== 0 ? rowValue / opValue : rowValue; break;
            default:  rowValue += opValue; break;
          }
        }
      }

      values.push(rowValue);
    }

    let result: number | null = null;

    if (values.length > 0) {
      switch (operation) {
        case 'average':
          result = values.reduce((a, b) => a + b, 0) / values.length;
          break;
        case 'count':
          result = values.length;
          break;
        case 'min':
          result = Math.min(...values);
          break;
        case 'max':
          result = Math.max(...values);
          break;
        case 'sum':
        default:
          result = values.reduce((a, b) => a + b, 0);
          break;
      }
    }

    // Apply post-aggregation operands
    const postOperands: CalcOperand[] = this.getConfigValue('pdfListCalcPostOperands', []);
    if (result !== null && postOperands && postOperands.length > 0) {
      for (const operand of postOperands) {
        let numValue: number | null;

        if (operand.sourceType === 'value') {
          numValue = operand.literalValue ?? null;
        } else {
          const rawValue = this.getOperandValue(operand);
          if (rawValue === null || rawValue === undefined) {
            continue;
          }
          numValue = this.parseNumericValue(rawValue);
        }

        if (numValue === null) {
          continue;
        }

        switch (operand.operator) {
          case '-': result -= numValue; break;
          case '*': result *= numValue; break;
          case '/': result = numValue !== 0 ? result / numValue : result; break;
          default:  result += numValue; break;
        }
      }
    }

    this.calculatedValue = result;
    this.formattedValue = result !== null ? this.formatValue(result, format, decimalPlaces) : '';

    // Store result if configured and value actually changed
    if (result !== null) {
      this.storeResult(result);
    }
  }

  private storeResult(result: number): void {
    const storeInContext = this.getConfigValue('pdfListCalcStoreInContext', false);
    if (!storeInContext || !this.viewManager) return;

    // Skip emit if value hasn't changed (prevents infinite loops)
    if (this.lastStoredValue === result) return;
    this.lastStoredValue = result;

    const storeMode: string = this.getConfigValue('pdfListCalcStoreMode', 'newVariable');

    if (storeMode === 'existingField') {
      // Store into a field of an existing context object
      const contextObject = this.getConfigValue('pdfListCalcStoreContextObject', '');
      const fieldName = this.getConfigValue('pdfListCalcStoreFieldName', '');
      if (!contextObject || !fieldName) return;

      const existingData = this.viewManager.contextData[contextObject];
      if (existingData && typeof existingData === 'object' && !Array.isArray(existingData)) {
        // Update the field in-place on the existing context object
        existingData[fieldName] = result;
        // Emit the entire context object so dependent cells refresh
        this.contextDataChange.emit({ [contextObject]: existingData });
      }
    } else {
      // Store as a new context variable
      const contextVariable = this.getConfigValue('pdfListCalcStoreContextVariable', '');
      const newVarFieldName = this.getConfigValue('pdfListCalcNewVariableFieldName', '');
      if (!contextVariable || !newVarFieldName) return;
      this.contextDataChange.emit({ [contextVariable]: { [newVarFieldName]: result } });
    }
  }

  private parseNumericValue(raw: any): number | null {
    if (typeof raw === 'number') {
      return isNaN(raw) ? null : raw;
    }
    if (typeof raw === 'string') {
      const cleaned = raw.replace(/[$€£¥,\s]/g, '');
      if (cleaned === '') return null;
      const num = Number(cleaned);
      return isNaN(num) ? null : num;
    }
    return null;
  }

  private getOperandValue(operand: CalcOperand): any {
    if (!this.viewManager?.contextData || !operand.contextVariable || !operand.fieldName) {
      return null;
    }

    const contextData = this.viewManager.contextData[operand.contextVariable];
    if (!contextData) {
      return null;
    }

    const record = firstContextRecord(contextData);
    return record ? record[operand.fieldName] : null;
  }

  private matchesFilters(item: any, filters: ListCalcFilter[]): boolean {
    for (const filter of filters) {
      if (!filter.fieldName || !filter.value) continue;
      const fieldValue = item[filter.fieldName];
      if (fieldValue === null || fieldValue === undefined) return false;

      const fieldStr = String(fieldValue).toLowerCase();
      const filterStr = filter.value.toLowerCase();

      switch (filter.operator) {
        case 'equals':
          if (fieldStr !== filterStr) return false;
          break;
        case 'not_equals':
          if (fieldStr === filterStr) return false;
          break;
        case 'contains':
          if (!fieldStr.includes(filterStr)) return false;
          break;
        case 'greater_than': {
          const a = parseFloat(fieldStr), b = parseFloat(filterStr);
          if (isNaN(a) || isNaN(b) || a <= b) return false;
          break;
        }
        case 'less_than': {
          const a = parseFloat(fieldStr), b = parseFloat(filterStr);
          if (isNaN(a) || isNaN(b) || a >= b) return false;
          break;
        }
      }
    }
    return true;
  }

  private formatValue(value: number, format: string, decimalPlaces: number): string {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces
        }).format(value);
      case 'percent':
        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces
        }).format(value);
      default:
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces
        }).format(value);
    }
  }
}
